/**
 * Builds login/signup query string preserving invite redirect and optional email prefill.
 */
export const buildAuthInviteSearch = (params: {
  redirectPath: string;
  email?: string | null;
}): string => {
  const search = new URLSearchParams();
  search.set("redirect", params.redirectPath);
  const email = params.email?.trim();
  if (email) {
    search.set("email", email);
  }
  return `?${search.toString()}`;
};

/**
 * True when the auth `redirect` query points at a workspace join invite link.
 */
export const isJoinInviteRedirect = (redirectUrl: string | null | undefined): boolean => {
  if (!redirectUrl) {
    return false;
  }
  try {
    const path = redirectUrl.startsWith("/")
      ? redirectUrl
      : new URL(redirectUrl, "https://app.rivetai.com").pathname;
    return path.includes("/join/");
  } catch {
    return redirectUrl.includes("/join/");
  }
};

/**
 * Workspace an accepted invite added the user to. Mirrors the backend: the
 * invitation's workspace, else the inviter's default workspace. Accepts the
 * invitation itself or a `{ data: invitation }` envelope.
 */
type AcceptedInvitation = {
  workspace_id?: string | null;
  inviter?: {
    default_workspace?: { workspace_id?: string | null; name?: string | null } | null;
  } | null;
};

const unwrapAcceptedInvitation = (accepted: unknown): AcceptedInvitation | null => {
  if (!accepted || typeof accepted !== "object") {
    return null;
  }
  const envelope = accepted as { data?: unknown };
  const invitation = envelope.data ?? accepted;
  return invitation && typeof invitation === "object" ? (invitation as AcceptedInvitation) : null;
};

export const getAcceptedInvitationWorkspaceId = (accepted: unknown): string | undefined => {
  const invitation = unwrapAcceptedInvitation(accepted);
  const workspaceId =
    invitation?.workspace_id || invitation?.inviter?.default_workspace?.workspace_id;
  return workspaceId ? String(workspaceId) : undefined;
};

/**
 * Name of the workspace an accepted invite added the user to. The accept
 * response only carries the inviter's default workspace, which can differ from
 * the invited one, so prefer the refreshed workspace catalog and trust the
 * inviter's default only when it is the invited workspace.
 */
export const getAcceptedInvitationWorkspaceName = (
  accepted: unknown,
  catalogWorkspaces: { workspace_id: string; name?: string | null }[] = []
): string | undefined => {
  const workspaceId = getAcceptedInvitationWorkspaceId(accepted);
  if (!workspaceId) {
    return undefined;
  }
  const fromCatalog = catalogWorkspaces
    .find((workspace) => String(workspace.workspace_id) === workspaceId)
    ?.name?.trim();
  if (fromCatalog) {
    return fromCatalog;
  }
  const inviterDefault = unwrapAcceptedInvitation(accepted)?.inviter?.default_workspace;
  if (inviterDefault?.workspace_id && String(inviterDefault.workspace_id) === workspaceId) {
    return inviterDefault.name?.trim() || undefined;
  }
  return undefined;
};

/**
 * Extracts invitation ID from a URL
 * @param url - The invitation URL
 * @returns The invitation ID or null if not found
 */
export const extractInvitationIdFromUrl = (_url: string): string | null => null;

/**
 * Checks if a URL is an invitation acceptance URL
 * @param url - The URL to check
 * @returns True if the URL is an invitation acceptance URL
 */
export const isInvitationUrl = (_url: string): boolean => false;

/**
 * Extracts redirect parameter from a URL
 * @param url - The URL to check
 * @returns The redirect URL or null if not found
 */
export const extractRedirectFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("redirect");
  } catch {
    return null;
  }
};

/**
 * Checks if a URL has a redirect parameter
 * @param url - The URL to check
 * @returns True if the URL has a redirect parameter
 */
export const hasRedirectParameter = (url: string): boolean => {
  return extractRedirectFromUrl(url) !== null;
};

/**
 * Extracts invitation token from a URL
 * @param url - The URL to check
 * @returns The invitation token or null if not found
 */
export const extractInvitationTokenFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split("/");
    const joinIndex = pathParts.indexOf("join");
    if (joinIndex !== -1 && pathParts[joinIndex + 1]) {
      return pathParts[joinIndex + 1];
    }
    return null;
  } catch {
    return null;
  }
};

// Storage keys for invitation persistence
const INVITATION_TOKEN_KEY = "pending_invitation_token";
const INVITATION_EMAIL_KEY = "pending_invitation_email";

/**
 * Storage utilities for invitation persistence
 */
export const invitationStorage = {
  /**
   * Store invitation token and email in localStorage
   */
  setPendingInvitation: (token: string, email?: string) => {
    try {
      localStorage.setItem(INVITATION_TOKEN_KEY, token);
      if (email) {
        localStorage.setItem(INVITATION_EMAIL_KEY, email);
      }
    } catch (error) {
      console.warn("Failed to store invitation token:", error);
    }
  },

  /**
   * Get pending invitation token from localStorage
   */
  getPendingInvitationToken: (): string | null => {
    try {
      return localStorage.getItem(INVITATION_TOKEN_KEY);
    } catch (error) {
      console.warn("Failed to get invitation token:", error);
      return null;
    }
  },

  /**
   * Get pending invitation email from localStorage
   */
  getPendingInvitationEmail: (): string | null => {
    try {
      return localStorage.getItem(INVITATION_EMAIL_KEY);
    } catch (error) {
      console.warn("Failed to get invitation email:", error);
      return null;
    }
  },

  /**
   * Clear pending invitation data from localStorage
   */
  clearPendingInvitation: () => {
    try {
      localStorage.removeItem(INVITATION_TOKEN_KEY);
      localStorage.removeItem(INVITATION_EMAIL_KEY);
    } catch (error) {
      console.warn("Failed to clear invitation data:", error);
    }
  },

  /**
   * Check if there's a pending invitation
   */
  hasPendingInvitation: (): boolean => {
    return invitationStorage.getPendingInvitationToken() !== null;
  },
};
